import { useCallback } from "react";

import { AccountViewer } from "components/form/graphql/types";
import useTypedContext from "hooks/useTypedContext";
import { Action, Stack } from "types/generated";
import { AccessResult, hasAtLeastOneRequiredAction, NO_ACCESS_MESSAGE } from "utils/access";
import { AccountContext } from "views/AccountWrapper";

import { checkTemplateDeploymentAccess } from "./helpers/checkTemplateDeploymentAccess";
import useHasStackAdminAccess from "./useHasStackAdminAccess";
import useHasStackWriteAccess from "./useHasStackWriteAccess";

const TRIGGER_ACCESS_TYPES = [Action.RunTrigger];

const hasTriggerAccess = (viewer: AccountViewer, spaceId: string) =>
  hasAtLeastOneRequiredAction(TRIGGER_ACCESS_TYPES, viewer, spaceId);

const TRIGGER_WITH_CUSTOM_CONFIG_ACCESS_TYPES = [Action.RunTriggerWithCustomRuntimeConfig];

const hasTriggerWithCustomConfigAccess = (viewer: AccountViewer, spaceId: string) =>
  hasAtLeastOneRequiredAction(TRIGGER_WITH_CUSTOM_CONFIG_ACCESS_TYPES, viewer, spaceId);

const STACK_LOCKED_BY_ANOTHER_USER_ERROR_MESSAGE =
  "You can't trigger a stack locked by another user";

const getTriggerStackErrorMessage = (
  isStackDisabled: boolean,
  isVcsDetached: boolean,
  hasAccess: boolean
) => {
  if (!hasAccess) {
    return NO_ACCESS_MESSAGE;
  }

  if (isStackDisabled) {
    return "You can't trigger a disabled stack";
  }

  if (isVcsDetached) {
    return "You can't trigger a stack that is not connected to a source code integration";
  }

  return NO_ACCESS_MESSAGE;
};

/**
 * Hook that returns a function to check if the current user can trigger a stack.
 * A user needs write access to trigger a stack.
 * A user can't trigger a disabled stack or a stack without VCS attachment.
 * A user can't trigger a stack that is locked by another user.
 * Stacks created from a template deployment (blueprintDeployment) cannot be triggered.
 * @returns {(stack: Stack, hasRuntimeConfig?: boolean) => AccessResult} Function that takes a stack and returns an AccessResult indicating if user can trigger it
 */
const useCanTriggerStack = (): ((stack: Stack, hasRuntimeConfig?: boolean) => AccessResult) => {
  const hasStackWriteAccessFn = useHasStackWriteAccess();
  const hasStackAdminAccessFn = useHasStackAdminAccess();
  const { viewer } = useTypedContext(AccountContext);

  return useCallback(
    (stack: Stack, hasRuntimeConfig?: boolean) => {
      const blueprintCheck = checkTemplateDeploymentAccess(stack);
      if (blueprintCheck) {
        return blueprintCheck;
      }

      const stackIsReadyToTrigger = !stack.isDisabled && !stack.vcsDetached;
      const hasAccessToTrigger =
        hasStackWriteAccessFn(stack).result ||
        hasTriggerAccess(viewer, stack.spaceDetails?.id || stack.space);
      const hasAccessToTriggerWithCustomConfig =
        hasStackAdminAccessFn(stack).result ||
        hasTriggerWithCustomConfigAccess(viewer, stack.spaceDetails?.id || stack.space);

      const hasAccess = hasRuntimeConfig ? hasAccessToTriggerWithCustomConfig : hasAccessToTrigger;

      const isLockedByMe = viewer.id === stack.lockedBy;
      if (stack.lockedBy && !isLockedByMe) {
        return {
          result: false,
          message: STACK_LOCKED_BY_ANOTHER_USER_ERROR_MESSAGE,
        };
      }

      if (!stackIsReadyToTrigger || !hasAccess) {
        return {
          result: false,
          message: getTriggerStackErrorMessage(stack.isDisabled, stack.vcsDetached, hasAccess),
        };
      }

      return {
        result: true,
      };
    },
    [viewer, hasStackWriteAccessFn, hasStackAdminAccessFn]
  );
};

export default useCanTriggerStack;
